import {  TrendingUp, TrendingDown, Globe, Sparkles } from 'lucide-react';
import { cn } from '../../../../utils/helpers';

export default function Analytics() {
  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-neutral-900 dark:text-neutral-50">Business Analytics</h1>
          <p className="text-neutral-500 dark:text-neutral-400">Real-time market insights and business performance metrics.</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
            Download Report
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm font-medium hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/20">
            <Sparkles className="w-4 h-4" />
            AI Insights
          </button>
        </div>
      </header>

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white dark:bg-neutral-900 p-8 rounded-[2.5rem] border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none">
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-bold text-lg">Revenue Performance</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-neutral-900 rounded-full"></div>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">Actual</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-neutral-200 dark:bg-neutral-800 rounded-full"></div>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">Target</span>
                </div>
              </div>
            </div>
            <div className="h-64 flex items-end gap-3">
              {[40, 60, 45, 90, 65, 80, 100, 85, 95, 70, 85, 100].map((h, i) => (
                <div key={i} className="flex-1 space-y-2">
                  <div className="w-full bg-neutral-50 dark:bg-neutral-950 h-full rounded-t-xl relative overflow-hidden">
                    <div 
                      className="absolute bottom-0 left-0 right-0 bg-neutral-900 rounded-t-xl transition-all duration-500"
                      style={{ height: `${h}%` }}
                    ></div>
                  </div>
                  <p className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 text-center uppercase tracking-widest">M{i+1}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-neutral-900 p-8 rounded-[2rem] border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg">Market Share</h3>
                <Globe className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
              </div>
              <div className="flex items-center justify-center py-4">
                <div className="w-32 h-32 rounded-full border-[12px] border-neutral-50 relative flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-[12px] border-neutral-900 border-t-transparent border-r-transparent rotate-45"></div>
                  <span className="text-2xl font-bold">24%</span>
                </div>
              </div>
              <div className="space-y-3 mt-4">
                <div className="flex justify-between text-xs font-bold">
                  <span className="text-neutral-500 dark:text-neutral-400">vs Competitors</span>
                  <span className="text-emerald-500">+4.2%</span>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-neutral-900 p-8 rounded-[2rem] border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg">Customer Growth</h3>
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="space-y-6">
                {[
                  { label: 'New Customers', value: '1,240', change: '+12%', color: 'bg-emerald-500' },
                  { label: 'Retention Rate', value: '85%', change: '+2%', color: 'bg-blue-500' },
                  { label: 'Churn Rate', value: '2.4%', change: '-0.5%', color: 'bg-rose-500' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 font-bold uppercase tracking-widest">{item.label}</p>
                      <p className="text-lg font-bold">{item.value}</p>
                    </div>
                    <span className={cn("text-xs font-bold px-2 py-1 rounded-lg", item.change.startsWith('+') ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400" : "bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400")}>
                      {item.change}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* AI Insights Sidebar */}
        <div className="space-y-6">
          <div className="bg-neutral-900 rounded-[2.5rem] p-8 text-white">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <h3 className="font-bold text-lg">AI Business Insights</h3>
            </div>
            <div className="space-y-6">
              {[
                { title: 'Optimization Opportunity', desc: 'Your operational costs in the tech sector are 15% higher than industry average.', action: 'View Analysis' },
                { title: 'Market Expansion', desc: 'Emerging demand in the healthcare sector aligns with your current product roadmap.', action: 'Explore Market' },
              ].map((insight, i) => (
                <div key={i} className="space-y-3 pb-6 border-b border-white/10 last:border-0 last:pb-0">
                  <p className="text-sm font-bold">{insight.title}</p>
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 leading-relaxed">{insight.desc}</p>
                  <button className="text-[10px] font-bold text-white uppercase tracking-widest hover:underline">
                    {insight.action}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-neutral-900 p-8 rounded-[2.5rem] border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none">
            <h3 className="font-bold mb-4">Competitor Tracking</h3>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-neutral-400 dark:text-neutral-500" />
                    </div>
                    <div>
                      <p className="text-sm font-bold">Competitor {i}</p>
                      <p className="text-[10px] text-neutral-500 dark:text-neutral-400">Market Share: {15 - i}%</p>
                    </div>
                  </div>
                  <TrendingDown className="w-4 h-4 text-rose-500 dark:text-rose-400" />
                </div>
              ))}
            </div>
            <button className="w-full mt-6 text-sm font-bold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">
              Full Competitive Analysis
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

import { Building2 } from 'lucide-react';
